import type { Product } from "./types";

/**
 * Metafield filter configuration
 */
export interface MetafieldConfig {
	key: string;
	namespace: string;
}

/**
 * Standard metafield configurations for product filtering
 */
export const METAFIELD_CONFIGS = {
	category: { key: "category", namespace: "custom" },
	dietary: { key: "dietary_preferences", namespace: "custom" },
	form: { key: "food_product_form", namespace: "custom" },
	allergen: { key: "allergen_information", namespace: "custom" },
	heating: { key: "heating_instructions", namespace: "custom" },
} as const satisfies Record<string, MetafieldConfig>;

/**
 * Parse a metafield value that may be a JSON array or a plain string.
 * Returns an array of strings in either case.
 */
export function parseMetafieldValue(
	value: string | undefined | null,
): string[] {
	if (!value) return [];
	try {
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? parsed : [value];
	} catch {
		return [value];
	}
}

/**
 * Get the raw string value of a product metafield by namespace + key.
 */
export function getMetafieldValue(
	product: Product,
	namespace: string,
	key: string,
): string | undefined {
	return product.metafields?.find(
		(m) => m?.namespace === namespace && m?.key === key,
	)?.value;
}

/**
 * Extract unique, sorted options from an array of products for a given metafield.
 * Used to build filter dropdown options.
 */
export function extractMetafieldOptions(
	products: Product[],
	config: MetafieldConfig,
): string[] {
	const options = new Set<string>();
	for (const product of products) {
		const value = getMetafieldValue(product, config.namespace, config.key);
		for (const v of parseMetafieldValue(value)) {
			options.add(v);
		}
	}
	return Array.from(options).sort();
}

/**
 * Check if a product's metafield contains the given filter value.
 * Handles both JSON array metafields and plain string values.
 */
export function productMatchesMetafieldFilter(
	product: Product,
	config: MetafieldConfig,
	filterValue: string,
): boolean {
	const value = getMetafieldValue(product, config.namespace, config.key);
	if (!value) return false;
	return parseMetafieldValue(value).includes(filterValue);
}
